import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { subscribeToGame, joinGame, updateGame, checkCanCreateGame, setTeamSelectionMode, selectTeam, confirmTeamSelection } from '../services/firebase';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import TeamSelectionPanel from '../components/TeamSelectionPanel';

const GameLobbyPage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuthStore();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameNotFound, setGameNotFound] = useState(false);
  const [playerName, setPlayerName] = useState(userProfile?.displayName || '');
  const [hasJoined, setHasJoined] = useState(false);

  useEffect(() => {
    if (!gameId) return;

    const unsubscribe = subscribeToGame(gameId, (gameData) => {
      if (gameData === null) {
        // Game doesn't exist
        setGameNotFound(true);
        setLoading(false);
        toast.error(`Game ${gameId} not found. Please check the game code.`, { duration: 5000 });
        setTimeout(() => navigate('/join'), 2000);
        return;
      }

      setGame(gameData);
      setLoading(false);

      if (gameData.status === 'playing') {
        navigate(`/game/${gameId}/play`);
      }
    });

    return () => unsubscribe();
  }, [gameId, navigate]);

  const handleJoin = async (e) => {
    e.preventDefault();
    try {
      // Check if user can play (has free games remaining or is premium)
      const canPlay = await checkCanCreateGame(user.uid);

      if (!canPlay.canCreate) {
        toast.error('You\'ve used all 3 free games. Upgrade to Premium for unlimited games!', {
          duration: 5000
        });
        setTimeout(() => navigate('/subscription'), 2000);
        return;
      }

      await joinGame(gameId, user.uid, playerName);
      setHasJoined(true);
      toast.success('Joined game!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleModeChange = async (mode) => {
    try {
      await setTeamSelectionMode(gameId, mode);
    } catch (error) {
      toast.error('Failed to change mode');
    }
  };

  const handleSelectTeam = async (team) => {
    try {
      await selectTeam(gameId, user.uid, team);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleConfirmTeam = async () => {
    try {
      await confirmTeamSelection(gameId, user.uid);
      toast.success('Team confirmed!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStartGame = async () => {
    try {
      await updateGame(gameId, { status: 'playing' });
      toast.success('Starting game!');
    } catch (error) {
      toast.error('Failed to start game');
    }
  };

  // Calculate team counts and validation for team selection mode
  const getTeamStats = () => {
    if (!game) return { teamA: 0, teamB: 0, confirmed: 0, total: 0 };

    const players = Object.values(game.players || {});
    const confirmations = game.playerTeamConfirmations || {};

    const teamA = players.filter(p => p.team === 'teamA').length;
    const teamB = players.filter(p => p.team === 'teamB').length;
    const confirmed = Object.keys(confirmations).filter(id => confirmations[id]).length;

    return { teamA, teamB, confirmed, total: players.length };
  };

  const canStartGame = () => {
    if (!game) return { can: false, reason: '' };

    const players = Object.values(game.players || {});
    const confirmations = game.playerTeamConfirmations || {};
    const stats = getTeamStats();

    // Minimum 4 players total
    if (stats.total < 4) {
      return { can: false, reason: `Need ${4 - stats.total} more players` };
    }

    // For team selection mode, check team requirements
    if (game.teamSelectionMode === 'teamSelection') {
      // Need at least 2 confirmed players per team
      const confirmedTeamA = players.filter(p => p.team === 'teamA' && confirmations[p.userId]).length;
      const confirmedTeamB = players.filter(p => p.team === 'teamB' && confirmations[p.userId]).length;

      if (confirmedTeamA < 2 || confirmedTeamB < 2) {
        const needA = Math.max(0, 2 - confirmedTeamA);
        const needB = Math.max(0, 2 - confirmedTeamB);
        if (needA > 0 && needB > 0) {
          return { can: false, reason: `Need ${needA} more in Team A and ${needB} more in Team B` };
        } else if (needA > 0) {
          return { can: false, reason: `Need ${needA} more confirmed in Team A` };
        } else {
          return { can: false, reason: `Need ${needB} more confirmed in Team B` };
        }
      }
    }

    return { can: true, reason: '' };
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading game...</div>;
  }

  if (gameNotFound || !game) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div className="card" style={{ textAlign: 'center', padding: '3rem', maxWidth: '500px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❌</div>
          <h2 style={{ marginBottom: '1rem' }}>Game Not Found</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
            Game code <strong>{gameId}</strong> doesn't exist. Please check the code and try again.
          </p>
          <a href="/join" className="btn btn-primary btn-full">
            Try Another Code
          </a>
        </div>
      </div>
    );
  }

  const isHost = game.hostId === user?.uid;
  const players = Object.values(game.players || {});
  const teamSelectionMode = game.teamSelectionMode || 'random';
  const startStatus = canStartGame();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', padding: '2rem 0' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ marginBottom: '0.5rem' }}>Game Lobby</h1>
            <div style={{
              display: 'inline-block',
              background: 'var(--primary)',
              color: 'white',
              padding: '0.5rem 1.5rem',
              borderRadius: 'var(--radius-lg)',
              fontSize: '1.5rem',
              fontWeight: 800,
              letterSpacing: '0.1em'
            }}>
              {gameId}
            </div>
            <p style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
              Share this code with friends to join
            </p>
          </div>

          {!hasJoined && !players.find(p => p.userId === user?.uid) ? (
            <form onSubmit={handleJoin} style={{ maxWidth: '400px', margin: '0 auto' }}>
              <div className="form-group">
                <label className="form-label">Your Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter your name"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-full btn-lg">
                Join Game
              </button>
            </form>
          ) : (
            <>
              {/* Team Selection Mode Toggle (Host Only) */}
              {isHost && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius-lg)'
                }}>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    marginBottom: '0.75rem',
                    color: 'var(--text-secondary)'
                  }}>
                    Team Assignment Mode
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem'
                  }}>
                    <button
                      onClick={() => handleModeChange('random')}
                      className={`btn ${teamSelectionMode === 'random' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ padding: '0.75rem' }}
                    >
                      <div style={{ fontWeight: 600 }}>Random Teams</div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                        Auto-assign players
                      </div>
                    </button>
                    <button
                      onClick={() => handleModeChange('teamSelection')}
                      className={`btn ${teamSelectionMode === 'teamSelection' ? 'btn-primary' : 'btn-outline'}`}
                      style={{ padding: '0.75rem' }}
                    >
                      <div style={{ fontWeight: 600 }}>Choose Teams</div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                        Players pick teams
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Show mode info for non-host players */}
              {!isHost && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem 1rem',
                  background: teamSelectionMode === 'teamSelection' ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius)',
                  textAlign: 'center',
                  fontSize: '0.875rem'
                }}>
                  {teamSelectionMode === 'teamSelection' ? (
                    <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                      Choose your team below
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Teams will be assigned randomly
                    </span>
                  )}
                </div>
              )}

              {/* Team Selection Panel (when in teamSelection mode) */}
              {teamSelectionMode === 'teamSelection' ? (
                <div style={{ marginBottom: '1.5rem' }}>
                  <TeamSelectionPanel
                    game={game}
                    userId={user?.uid}
                    onSelectTeam={handleSelectTeam}
                    onConfirm={handleConfirmTeam}
                    showConfirmButton={true}
                    disabled={false}
                    compact={false}
                  />
                </div>
              ) : (
                /* Player List (Random mode) */
                <>
                  <h3 style={{ marginBottom: '1rem' }}>
                    Players ({players.length})
                  </h3>
                  <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '2rem' }}>
                    {players.map((player) => (
                      <div
                        key={player.userId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          padding: '1rem',
                          background: 'var(--bg-tertiary)',
                          borderRadius: 'var(--radius)'
                        }}
                      >
                        <div className="avatar">
                          {player.name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{player.name}</div>
                          {player.userId === game.hostId && (
                            <span className="badge badge-primary" style={{ fontSize: '0.75rem' }}>Host</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Start Game Button (Host Only) */}
              {isHost && (
                <button
                  onClick={handleStartGame}
                  disabled={!startStatus.can}
                  className="btn btn-primary btn-full btn-lg"
                >
                  {startStatus.can ? 'Start Game →' : startStatus.reason}
                </button>
              )}

              {/* Waiting message for non-host */}
              {!isHost && (
                <div style={{
                  padding: '1rem',
                  background: 'var(--bg-tertiary)',
                  borderRadius: 'var(--radius)',
                  textAlign: 'center',
                  marginTop: teamSelectionMode === 'teamSelection' ? '0' : undefined
                }}>
                  Waiting for host to start game...
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <a href="/dashboard" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
            ← Leave lobby
          </a>
        </div>
      </div>
    </div>
  );
};

export default GameLobbyPage;
